import { keyBy } from 'lodash';
import compare from 'just-compare';
import clone from 'just-clone';
import { createDebouncer } from '~/js/utils';
import {
  fetchProducts,
  fetchCollection,
  fetchSearch,
  fetchColorMetaobjects,
} from '~/js/utils/products-and-collections';

const RESULTS_PER_PAGE = 12;

const productsBrowser = (context, collectionHandle) => ({
  context,
  isLoading: false,
  isLoadingInitial: true,
  // data will be Shopify StoreFront API response
  // https://shopify.dev/docs/custom-storefronts/building-with-the-storefront-api/products-collections/filter-products#step-1-query-products
  data: null,
  error: '',
  activeFacets: {},
  activeFacetsOld: {},
  sort: '',
  sortOld: '',
  currentPage: 1,
  loadId: 0,
  ignoreUpdates: false,
  filtersDesktopVisible: true,
  printCollectionsState: {},
  afterCursor: null,
  currentFilters: {},
  filtersObj: {},
  swatchesMetaobjectIds: {},
  swatchesMetaobjectKeys: {},
  sortingOptions: [
    context === 'collection' && { field: 'BEST_SELLING', direction: 'desc', label: 'Bestsellers', reverse: false },
    { field: 'RELEVANCE', label: 'Relevance', direction: 'desc', reverse: false },
    { field: 'PRICE', direction: 'asc', label: 'Price Low to High', reverse: false },
    { field: 'PRICE', direction: 'desc', label: 'Price High to Low', reverse: true },
  ].filter(Boolean),

  get products() {
    const products = (this.data?.products.nodes || []).map((node) => ({ product: node, type: 'product' }));
    // Commenting as this causes the search to break
    // const collectionFields = this.data.metafields[0]?.references.nodes.map((node) => {
    //   const order = parseInt(node?.block_position.value) - 1;
    //   if (order >= 0) {
    //     products.splice(order, 0, { product: node, type: 'collection-card' });
    //   }
    // });
    return products;
  },

  get facets() {
    const filters = this.data?.products.filters;
    const result = (filters || []).map((facet) => {
      if (this.activeFacets[facet.id] && this.activeFacets[facet.id].length) {
        facet.expanded = true;
      }
      if (facet.id.startsWith('filter.v.m.custom.color_swatch')) {
        facet.type = 'SWATCH';
        facet.values = facet.values.map((value) => {
          const m = this.swatchesMetaobjectIds;
          const { variantMetafield } = JSON.parse(value.input);
          const swatchObj = m[variantMetafield.value];
          return {
            ...value,
            swatch: swatchObj,
          };
        });
      } else if (facet.id === 'filter.v.availability') {
        facet.type = 'RADIO';
      }
      return facet;
    });
    this.filtersObj = keyBy(result, 'id');
    return result || [];
  },

  get hasLoadMore() {
    const pageInfo = this.data?.products?.pageInfo;
    return pageInfo?.hasNextPage;
  },

  get hasNoActiveFacetsAndEmptyResults() {
    if (this.isLoadingInitial || this.isLoading) return false;

    return this.products.length === 0 && Object.values(this.activeFacets).every((value) => !value.length);
  },

  async init() {
    this.debounceNextTick = createDebouncer(this.$nextTick.bind(this));
    this.setStateFromUrl();
    try {
      await this.queryProducts();
    } finally {
      this.isLoadingInitial = false;
    }

    this.$watch(
      'activeFacets',
      this.debounceNextTick((value) => {
        // we have the code that in some cases updated the activeFacets immediately
        // so we wait one tick before fetching products

        if (this.ignoreUpdates) return;
        if (this.hasNoActiveFacetsAndEmptyResults) return;
        // TODO: UI for this
        if (!compare(value, this.activeFacetsOld)) {
          this.currentPage = 1;
          this.queryProducts();
        }
      }),
    );

    this.$watch('ignoreUpdates', (value) => {
      if (value) {
        this.activeFacetsCopy = clone(this.activeFacets);
        this.sortCopy = this.sort;
      } else {
        this.activeFacets = this.activeFacetsCopy;
        this.sort = this.sortCopy;
      }
    });

    this.$watch('sort', (value) => {
      if (value === this.sortOld) return;
      if (this.ignoreUpdates) return;

      this.queryProducts();
    });
  },
  async queryProducts({ loadMore = false } = {}) {
    const loadId = ++this.loadId;

    const [sortField, sortDirection] = this.sort.split('::');

    const searchParams = new URLSearchParams({
      page: this.currentPage,
    });

    if (context === 'search-results') {
      searchParams.set('q', new URLSearchParams(window.location.search).get('q') || '');
    }

    let reverse = false; // to reverse the price sorting
    if (sortField && sortDirection) {
      searchParams.append(`sort.${sortField}`, sortDirection);
      if (sortField === 'PRICE' && sortDirection === 'desc') {
        reverse = true;
      }
    }
    const swatches = await fetchColorMetaobjects();
    if (swatches) {
      const swatchesNode = swatches.nodes;
      const swatchesIDs = keyBy(swatchesNode, 'id');
      const swatchesKeys = keyBy(swatchesNode, 'color_name.value');
      Object.assign(this.swatchesMetaobjectIds, swatchesIDs);
      Object.assign(this.swatchesMetaobjectKeys, swatchesKeys);
    }
    const filtersArray = [];
    // take the activefacets and convert into filtersArray which would be fed into the collection query
    Object.entries(this.activeFacets).forEach(([key, values]) => {
      // filtersObj will have value when we go for filtering but not when we are reloading the page.
      if (values && values.length) {
        values.forEach((value) => {
          const productMetafieldRegex = new RegExp(/filter.p.m.([\w]+).([\w]+)/);
          if (key.match(productMetafieldRegex)) {
            // eslint-disable-next-line
            const [_, namespace, mfKey] = key.match(productMetafieldRegex);
            filtersArray.push({ productMetafield: { namespace, key: mfKey, value } });
            searchParams.append(key, value);
          }
          const variantMetafieldRegex = new RegExp(/filter.v.option.([\w]+)/);
          if (key.match(variantMetafieldRegex)) {
            // eslint-disable-next-line
            const [_, name] = key.match(variantMetafieldRegex);
            filtersArray.push({ variantOption: { name, value } });
            searchParams.append(key, value);
          }
          const productVariantMetafieldRegex = new RegExp(/filter.v.m.([\w]+).([\w]+)/);
          if (key.match(productVariantMetafieldRegex)) {
            // eslint-disable-next-line
            const [_, namespace, mfKey] = key.match(productVariantMetafieldRegex);
            searchParams.append(key, value);
            if (mfKey === 'color_swatch') {
              value = this.swatchesMetaobjectKeys[value].id;
            }
            filtersArray.push({ variantMetafield: { namespace, key: mfKey, value } });
          }
          if (key === 'filter.v.availability') {
            const valueBoolean = value === 'In stock';
            filtersArray.push({ available: valueBoolean });
            searchParams.append(key, value);
          }
          if (value && key === 'filter.v.price') {
            const obj = typeof value === 'string' ? JSON.parse(value) : value;
            filtersArray.push({ price: { min: +obj.min, max: +obj.max } });
            searchParams.append(`${key}.min`, obj.min);
            searchParams.append(`${key}.max`, obj.max);
          }
          if (key === 'filter.v.price.min' || key === 'filter.v.price.max') {
            // find if there's a key by price if not add to the filtersArray otherwise update the value
            const priceFilter = filtersArray.find((filter) => filter.price);
            if (!priceFilter) {
              if (key === 'filter.v.price.min') {
                filtersArray.push({ price: { min: value, max: 0 } });
              }
              filtersArray.push({ price: { min: 0, max: 0 } });
            }
            if (priceFilter) {
              if (key === 'filter.v.price.min') {
                priceFilter.price.min = +value;
              } else {
                priceFilter.price.max = +value;
              }
            }
          }
        });
      }
    });

    this.activeFacetsOld = clone(this.activeFacets);
    this.sortOld = this.sort;

    try {
      this.isLoading = true;
      this.error = '';

      const args = [
        context === 'collection' ? collectionHandle : context === 'search-results' ? searchParams.get('q') : '',
        loadMore ? this.afterCursor : null,
        RESULTS_PER_PAGE,
        filtersArray,
        sortField,
        reverse,
      ];

      const data = await (context === 'collection' ? fetchCollection(...args) : fetchSearch(...args));
      try {
        // to read the products in the global store.
        await fetchProducts(data.products.nodes.map((obj) => obj.id));
      } catch (e) {
        // if we fail here we'll retry and display error inside product card
        // eslint-disable-next-line no-console
        console.warn(e);
      }
      if (loadId !== this.loadId) return;
      const oldData = this.data;
      this.$dispatch('products-browser-data', { searchParams, data });
      this.data = clone(data);
      if (loadMore) {
        this.data.products.nodes = [...oldData.products.nodes, ...this.data.products.nodes];
      }
      this.activeFacets = { ...this.getEmptyFacets(), ...this.activeFacets };
      this.activeFacetsOld = clone(this.activeFacets);
      this.sortOld = this.sort;
      if (this.data.products.pageInfo.hasNextPage) {
        this.afterCursor = this.data.products.pageInfo.endCursor;
      }

      this.updateUrl(searchParams);
    } catch (e) {
      // TODO: analytics? sentry?
      this.error = e.message;
      throw this.error;
    } finally {
      if (loadId === this.loadId) {
        this.isLoading = false;
      }
    }
  },

  getEmptyFacets() {
    return Object.fromEntries(this.data.products.filters.map((filter) => [filter.id, []]));
  },

  updateUrl(searchParams) {
    const url = new URL(window.location.href);
    const currentParams = new URLSearchParams(window.location.search);
    url.search = new URLSearchParams(searchParams);

    const firstSort = this.sortingOptions[0];
    if (searchParams.get(`sort.${firstSort.field}`) === firstSort.direction) {
      url.searchParams.delete(`sort.${firstSort.field}`);
    }

    Array.from(url.searchParams.keys()).forEach((key) => {
      if (key.startsWith('bgfilter.')) {
        url.searchParams.delete(key);
      }
    });

    url.searchParams.delete('page'); // no classic pagination

    const currentParamsEntries = Array.from(currentParams.entries());
    const newParamsEntries = Array.from(url.searchParams.entries());
    if (
      currentParamsEntries.length !== newParamsEntries.length ||
      currentParamsEntries.some(([, value], index) => value !== newParamsEntries[index][1])
    ) {
      window.history.pushState({}, '', url.toString());
    }
  },

  // to reconstruct the activeFacets object from the queryParams in the URL
  setStateFromUrl() {
    const activeFacets = this.data ? this.getEmptyFacets() : {};
    this.currentPage = 1;

    const firstSort = this.sortingOptions[0];
    if (firstSort) {
      this.sort = `${firstSort.field}::${firstSort.direction}`;
    }
    /* we need to recreate the object that comes with the activeFacets.
     * so for productMetafields the object is {productMetafield: {namespace: 'custom', key: 'category', value: '<coming from URL param>'}}
     * for variantOption the object is {variantOption: {name: 'size', value: 'S'}}
     * convert filter.p.m.custom.category => {productMetafield: {namespace: 'custom', key: 'category', value: '<coming from URL param>'}}
     * convert filter.v.option.size => variantOption: {name: 'size', value: 'S'}}
     * activeFacets is an object like this
     * {
     * filter.p.m.custom.category: ['Bags']
     * filter.v.option.size: []
     * }
     */
    // we can write simple Regex to convert filter.p.m.custom.category => {productMetafield: {namespace: 'custom', key: 'category', value: '<coming from URL param>'}
    Array.from(new URLSearchParams(window.location.search)).forEach(([key, value]) => {
      if (key.startsWith('filter.')) {
        if (key.includes('filter.v.price')) {
          if (!activeFacets['filter.v.price']) {
            activeFacets['filter.v.price'] = [];
          }
          const priceRegex = new RegExp(/filter.v.price.([\w]+)/);
          const [, priceKey] = key.match(priceRegex);
          activeFacets['filter.v.price'].length === 0
            ? activeFacets['filter.v.price'].push({ [priceKey]: +value })
            : (activeFacets['filter.v.price'][0][priceKey] = +value);
        } else if (!activeFacets[key]) {
          activeFacets[key] = [value];
        } else {
          activeFacets[key].push(value);
        }
      } else if (key.startsWith('sort.')) {
        const sortField = key.replace('sort.', '');
        const sortDirection = value;
        this.sort = `${sortField}::${sortDirection}`;
      } else if (key === 'page') {
        // ignore page from url
        // this.currentPage = Number(value);
      }
    });

    this.activeFacets = activeFacets;
  },

  handlePopstate() {
    this.setStateFromUrl();
  },

  loadMore() {
    this.currentPage += 1;
    return this.queryProducts({ loadMore: true });
  },

  clearAllFacets() {
    this.activeFacets = this.getEmptyFacets();
    this.queryProducts();
  },

  removeFilter(filter) {
    if (Array.isArray(this.activeFacets[filter.field])) {
      this.activeFacets[filter.field] = this.activeFacets[filter.field].filter((v) => v !== filter.value);
    } else {
      this.activeFacets[filter.field] = [];
    }
  },
});

export default productsBrowser;
